import { teacherToolsService } from '../../../../services';
import { getLessons } from 'core/crud/lessons.crud';
import * as coreEntities from 'core/store/index';
import { successResponse } from 'core/utils/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const useTextInputOrLessonSelector = ({ value, onChange, wordLimit = 2000 }) => {
  const dispatch = useDispatch();

  const [mode, setMode] = useState(value?.mode || 'text');
  const [textValue, setTextValue] = useState(value?.text || '');
  const [selectedLesson, setSelectedLesson] = useState(value?.lesson || null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [units, setUnits] = useState([]);
  const [loadingLessonContent, setLoadingLessonContent] = useState(false);
  const [contentError, setContentError] = useState(null);

  // Use actual courses instead of teacher course templates
  const courses = useSelector((state) => coreEntities.courses.selectors.getCourses(state));

  useEffect(() => {
    if (value) {
      setMode(value.mode || 'text');
      setTextValue(value.text || '');
      setSelectedLesson(value.lesson || null);
    }
  }, []);

  useEffect(() => {
    dispatch(coreEntities.courses.actions.getCourses({ params: { offset: 0, pageSize: 1000 } }));
  }, []);

  const wordCount = useMemo(() => {
    return textValue.trim() ? textValue.trim().split(' ').length : 0;
  }, [textValue]);

  const handleTextChange = useCallback(
    (text) => {
      setMode('text');
      setTextValue(text);
      setSelectedLesson(null);

      const currentWordCount = text.trim() ? text.trim().split(' ').length : 0;
      const isValid = currentWordCount <= wordLimit && currentWordCount > 0;

      onChange({
        mode: 'text',
        text: text,
        lesson: null,
        isValid: isValid,
      });
    },
    [wordLimit, onChange]
  );

  const handleLessonSelect = useCallback(
    async (lesson) => {
      setMode('lesson');
      setTextValue('');
      setIsDialogOpen(false);

      // Set the lesson immediately without word count
      setSelectedLesson({
        ...lesson,
        wordCount: null, // Set to null to indicate loading
      });

      setLoadingLessonContent(true);
      setContentError(null); // Clear any previous errors

      let lessonContent = '';
      let calculatedWordCount = 0;
      const lessonId = lesson?.lessonGuid || lesson?.guid;

      try {
        const response = await teacherToolsService.getLessonContent(lessonId);
        lessonContent = response?.content || response?.text || '';
        lessonContent = lessonContent.trim();

        // Check if content is empty
        if (!lessonContent || lessonContent === '') {
          setContentError('empty');
          return;
        }
        setTextValue(lessonContent);
        calculatedWordCount = lessonContent ? lessonContent.split(/\s+/).length : 0;
        setSelectedLesson({
          ...lesson,
          wordCount: calculatedWordCount,
        });
      } catch (error) {
        console.error('[TextInputOrLessonSelector] Failed to fetch lesson content:', error);
        setContentError('failed');
      } finally {
        setLoadingLessonContent(false);
      }

      // Pass the calculated word count and isValid in the onChange callback
      const isValid = calculatedWordCount <= wordLimit && calculatedWordCount > 0;

      onChange({
        mode: 'lesson',
        text: lessonContent,
        lesson: {
          ...lesson,
          lessonId: lessonId,
          wordCount: calculatedWordCount, // Include the calculated word count
        },
        isValid: isValid,
      });
    },
    [onChange, wordLimit]
  );

  const handleRemoveLesson = useCallback(() => {
    setMode('text');
    setSelectedLesson(null);
    setTextValue(''); // Clear the text content
    setContentError(null); // Clear any errors

    // Notify parent - isValid is false since text is empty
    onChange({
      mode: 'text',
      text: '', // Pass empty string instead of textValue
      lesson: null,
      isValid: false,
    });
  }, [onChange]);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const loadCourseUnits = useCallback(async (courseGuid) => {
    setLoadingUnits(true);
    setUnits([]);

    try {
      // Pass only guid, no extra params like kanban or dates
      const response = await getLessons({ guid: courseGuid });
      if (successResponse(response) && response.data?.data?.items) {
        // The response.data.data.items is an array of units
        // Each unit has a 'section' property with unit info and 'items' array with lessons
        const courseUnits = response.data.data.items || [];
        setUnits(courseUnits);
      }
    } catch (error) {
      console.error('Error loading units:', error);
      setUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  }, []);

  return {
    mode,
    textValue,
    selectedLesson,
    isDialogOpen,
    wordCount,
    wordLimit,
    loadingUnits,
    units,
    loadingLessonContent,
    contentError,

    courses: courses || [],

    handleTextChange,
    handleLessonSelect,
    handleRemoveLesson,
    openDialog,
    closeDialog,
    loadCourseUnits,
  };
};

export default useTextInputOrLessonSelector;
